import { Button, Divider, Skeleton, Switch } from "antd"
import alarmIcon from "assets/svg/alarm.svg"
import buildingIcon from "assets/svg/Buildings.svg"
import mapIcon from "assets/svg/MapPinArea.svg"
import lecternIcon from "assets/svg/Lectern.svg"
import powerIcon from "assets/svg/power.svg"
import { ReactNode } from "react"
import { useNavigate } from "react-router-dom"
import { IWorkPlacesEntity } from "../typings"
import SingleLineSkeleton from "./SingleLineSkeleton"
import { getStaticMapUrl } from "components/GeoPicker"
import usePermissions from "hooks/usePermissions"
import { TimeAndAttendancePermission } from "constants/permissions"
import { useAppNotificationStore } from "stores"

type Props = {
  status: string
  office: string
  address: string
  recordsStatusCount: number
  employeeCount: number
  dutyLocationsCount: number
  shiftPlansCount: number
  onToggle: () => void
  workplaceDetail: IWorkPlacesEntity
  workplaceId: number | undefined
}
const WorkPlaceCard: React.FC<Props> = ({
  status,
  office,
  address,
  recordsStatusCount,
  employeeCount,
  dutyLocationsCount,
  shiftPlansCount,
  onToggle,
  workplaceDetail,
  workplaceId,
}) => {
  const navigate = useNavigate()
  const { toast } = useAppNotificationStore()
  const [canManageWorkplace, canViewRecords, canUpload] = usePermissions({
    allowedPermissions: [
      TimeAndAttendancePermission.CanCreateWorkplaces,
      TimeAndAttendancePermission.CanViewAttendanceRecords,
      TimeAndAttendancePermission.CanUploadTimesheets,
    ],
  })

  return (
    <>
      <div
        className={`border-2 border-slate-200 rounded-lg w-full h-fit ${
          status.toLowerCase() === "draft" ? "pb-4" : "pb-2"
        } overflow-hidden`}
      >
        <div className="relative w-full h-36">
          <div
            className="w-full h-36 bg-center bg-no-repeat bg-cover"
            style={{
              backgroundImage: `url("${getStaticMapUrl(
                Number(workplaceDetail?.latitude!),
                Number(workplaceDetail?.longitude!)
              )}")`,
            }}
          >
            <div className="flex justify-end items-center gap-1 p-4">
              <Switch
                checked={
                  workplaceDetail?.workplaceConfig?.status === "published"
                }
                onChange={() => {
                  if (!canManageWorkplace) {
                    toast.unauthorized()
                    return
                  }
                  onToggle()
                }}
                checkedChildren={
                  <span style={{ color: "#FFFFFF", fontSize: 12 }}>
                    Published
                  </span>
                }
                unCheckedChildren={
                  <span style={{ color: "#0F172A", fontSize: 12 }}>Draft</span>
                }
                style={{
                  backgroundColor:
                    workplaceDetail?.workplaceConfig?.status === "published"
                      ? "#004AF5"
                      : "#E2E8F0",
                  ["--handle-icon" as string]: `url(${powerIcon})`,
                }}
                className="[&_.ant-switch-handle::before]:!bg-[image:var(--handle-icon)] [&_.ant-switch-handle::before]:!bg-no-repeat [&_.ant-switch-handle::before]:!bg-center"
              />
            </div>
          </div>
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-12 z-10"
            style={{
              background:
                "linear-gradient(to bottom, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 100%)",
            }}
          />
        </div>

        <div className="px-4 flex flex-col gap-4">
          <div className="flex flex-col gap-3 border-b border-b-slate-300 pb-4">
            <div className="flex justify-between items-center">
              <h5 className="text-xl font-bold truncate text-slate-900">
                {capitalize(office)}
              </h5>
              {status.toLowerCase() !== "drafts" && (
                <div className="flex items-center gap-1">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 21a9.004 9.004 0 0 0 8.716-6.747M12 21a9.004 9.004 0 0 1-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 0 1 7.843 4.582M12 3a8.997 8.997 0 0 0-7.843 4.582m15.686 0A11.953 11.953 0 0 1 12 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0 1 21 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0 1 12 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 0 1 3 12c0-1.605.42-3.113 1.157-4.418"
                    />
                  </svg>
                  <span className="text-slate-300">/</span>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth="1.5"
                    stroke="currentColor"
                    className="w-4 h-4"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M10.5 19.5h3m-6.75 2.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-15a2.25 2.25 0 0 0-2.25-2.25H6.75A2.25 2.25 0 0 0 4.5 4.5v15a2.25 2.25 0 0 0 2.25 2.25Z"
                    />
                  </svg>
                  <span className="text-slate-300">/</span>
                  <img
                    width={15}
                    height={15}
                    src={lecternIcon}
                    alt="building icon"
                  />
                </div>
              )}
            </div>
            <div className="text-sm flex justify-between items-center gap-2">
              <span className="min-w-0 flex flex-1 items-center gap-1">
                <img
                  width={16}
                  height={16}
                  src="https://padehrs3-staging.s3.us-east-2.amazonaws.com/images/48df13a3-99f6-4fd1-ace4-46218cd5b685.svg"
                  alt="building icon"
                />
                {status.toLowerCase() === "draft" ? (
                  <SingleLineSkeleton />
                ) : (
                  <p className="truncate text-xs text-slate-900">
                    {capitalize(address)}
                  </p>
                )}
              </span>
              {status.toLowerCase() !== "draft" && (
                <p
                  className={"shrink-0 text-xs"}
                  style={{
                    color:
                      recordsStatusCount === null || recordsStatusCount === 0
                        ? "#16A34A"
                        : "#FB2C36",
                  }}
                >
                  {recordsStatusCount === null || recordsStatusCount === 0
                    ? "Record Up to Date"
                    : `Records ${recordsStatusCount} Days behind`}
                </p>
              )}
            </div>
          </div>
          <div className="text-sm flex items-center justify-between lg:gap-2">
            <EmployeeDutyShiftsWrapper
              image={
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-4 h-4"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M15 19.128a9.38 9.38 0 0 0 2.625.372 9.337 9.337 0 0 0 4.121-.952 4.125 4.125 0 0 0-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 0 1 8.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0 1 11.964-3.07M12 6.375a3.375 3.375 0 1 1-6.75 0 3.375 3.375 0 0 1 6.75 0Zm8.25 2.25a2.625 2.625 0 1 1-5.25 0 2.625 2.625 0 0 1 5.25 0Z"
                  />
                </svg>
              }
              count={employeeCount}
              plan="employee"
              status={status}
            />
            <DividerVertical />
            <EmployeeDutyShiftsWrapper
              image={mapIcon}
              count={dutyLocationsCount}
              plan="duty locations"
              status={status}
            />
            <DividerVertical />
            <EmployeeDutyShiftsWrapper
              image={alarmIcon}
              count={shiftPlansCount}
              plan="shift plans"
              status={status}
            />
          </div>

          {status.toLowerCase() === "draft" && (
            <div className="px-2">
              <Button
                ghost
                style={{
                  borderColor: "#CAD5E2",
                  color: "#334155",
                  width: "100%",
                }}
                className="text-sm flex items-center gap-2 justify-center font-medium"
                onClick={() => {
                  if (!canManageWorkplace) {
                    toast.unauthorized()
                    return
                  }
                  navigate(
                    `/dashboard/time-and-attendance/setup?workplaceId=${workplaceId}`
                  )
                }}
              >
                <img
                  width={15}
                  height={15}
                  src={buildingIcon}
                  alt="alarm icon"
                />
                Finish Configuration
              </Button>
            </div>
          )}
          {status.toLowerCase() !== "draft" && (
            <div className="flex flex-col justify-center items-center gap-1 pt-2">
              <Button
                ghost
                style={{
                  borderColor: "#CAD5E2",
                  color: "#334155",
                  width: "100%",
                }}
                className="text-sm flex items-center gap-2 justify-center font-medium"
                onClick={() => {
                  if (!canViewRecords) {
                    toast.unauthorized()
                    return
                  }
                  navigate(
                    `/dashboard/time-and-attendance/view-workplace/${workplaceDetail.id}`
                  )
                }}
              >
                <img
                  width={20}
                  height={20}
                  src={buildingIcon}
                  alt="building icon"
                />
                View workplace
              </Button>
              <Button
                type="text"
                ghost
                style={{
                  width: "100%",
                }}
                className="text-sm flex items-center gap-2 justify-center font-medium"
                onClick={() => {
                  if (!canUpload) {
                    toast.unauthorized()
                    return
                  }
                  navigate("/dashboard/time-and-attendance/upload-timesheet")
                }}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  className="w-5 h-5"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
                  />
                </svg>
                Upload timesheet
              </Button>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default WorkPlaceCard

export const DividerVertical = () => {
  return (
    <div className="border-l border-[#CAD5E2] h-6 mx-2 transform rotate-12"></div>
  )
}

type EmployeeDutyShiftsWrapperProps = {
  image: string | ReactNode
  count: number
  plan: string
  status?: string
}
export const EmployeeDutyShiftsWrapper: React.FC<
  EmployeeDutyShiftsWrapperProps
> = ({ image, count, plan, status }) => {
  if (status === undefined) return null
  return (
    <span className="flex items-center gap-1">
      {typeof image === "string" ? (
        <img
          width={15}
          height={15}
          src={image}
          alt={image}
        />
      ) : (
        image
      )}
      {status!.toLowerCase() === "draft" ? (
        <div className="w-16 min-w-0">
          {" "}
          <SingleLineSkeleton />
        </div>
      ) : (
        <p className="text-xs text-slate-500 truncate md:overflow-visible md:whitespace-normal min-w-0">
          {count} {plan}
        </p>
      )}
    </span>
  )
}

const capitalize = (text: string) =>
  text.charAt(0).toUpperCase() + text.slice(1).toLowerCase()
